import api from "api";
import { delay } from "redux-saga";
import {
  all,
  call,
  cancel,
  cancelled,
  fork,
  put,
  take,
  takeLatest
} from "redux-saga/effects";

import { policies } from "../actions";
import {
  COMMIT_POLICIES_START,
  FETCH_POLICIES_START,
  REMOVE_POLICY_CANCEL,
  REMOVE_POLICY_START,
  UNDO_REMOVE_POLICY_TIMER
} from "../constants";
import { addChangesetSaga } from "./changesets";
import { fetchPagedSagaFactory, REFRESH_DELAY } from "./utils";

export const policiesSaga = fetchPagedSagaFactory(
  api.Policies.get,
  policies.success,
  policies.failed
);

export function* addPolicySaga(policy, token) {
  let result;
  try {
    const { response } = yield call(api.Policies.create, policy, {
      "X-Crbs-Changeset-Token": token
    });
    result = response;
    yield put(policies.commitAddSuccess(result));
  } catch (error) {
    // CONFLICT is okay here if policy already exists,
    // we just carry on.
    if (error.code !== 409) {
      yield put(policies.commitFailed(error));
    }
  }
  return result;
}

export function* commitPoliciesSaga(action) {
  try {
    const response = yield addChangesetSaga({ message: action.message });
    const policiesToAdd = action.policies.filter(
      policy => policy.requiresUpdate === true
    );
    const policiesToDelete = action.policies.filter(
      policy => policy.requiresDelete === true
    );
    if (policiesToAdd.length > 0) {
      yield all(policiesToAdd.map(p => call(addPolicySaga, p, response.token)));
    }
    if (policiesToDelete.length > 0) {
      yield all(policiesToDelete.map(p => call(api.Policies.delete, p)));
      yield put(policies.commitDeleteSuccess(policiesToDelete));
    }
    yield put(policies.closeModal());
    // Ensure server state has updated,
    // before refreshing.
    yield call(delay, REFRESH_DELAY);
    // refresh policies
    yield put(policies.request());
  } catch (error) {
    yield put(policies.commitFailed(error));
  }
}

export function* removePolicySaga(policy) {
  try {
    // Wait and see whether removePolicyMainSaga cancels this saga.
    yield call(delay, UNDO_REMOVE_POLICY_TIMER);
    // If cancelled, dispatch action to put policy back in state. Otherwise,
    // remove policy from server.
    if (yield cancelled()) {
      yield put(policies.removeCancel(policy));
    } else {
      yield call(api.Policies.delete, policy);
      yield put(policies.removeSuccess(policy));
      yield call(delay, REFRESH_DELAY);
      yield put(policies.request());
    }
  } catch (error) {
    yield put(policies.removeFailed(error, policy));
  }
}

export function* removePolicyMainSaga(action) {
  try {
    const remove = yield fork(removePolicySaga, action.policy);
    // If REMOVE_POLICY_CANCEL is detected before timer is up, cancel removal
    // process and refresh policies state.
    yield take(REMOVE_POLICY_CANCEL);
    yield cancel(remove);
    yield put(policies.request());
  } catch (error) {
    yield put(policies.removeFailed(error, action.policy));
  }
}

export function* watchPolicies() {
  yield takeLatest(FETCH_POLICIES_START, policiesSaga);
}

export function* watchCommitPolicies() {
  yield takeLatest(COMMIT_POLICIES_START, commitPoliciesSaga);
}

export function* watchRemovePolicy() {
  yield takeLatest(REMOVE_POLICY_START, removePolicyMainSaga);
}
